import type { Status, Consent } from 'cookieconsent'
import 'cookieconsent/build/cookieconsent.min.css'
declare global {
  interface Window {
    cookieconsent: Consent
  }
}
export default defineNuxtPlugin((NuxtApp) => {
  // 非 EU 站没有 CMP，没人会把 ze-snippet 的 type 翻回来（见 nuxt.config.ts 的说明）。
  // 该属性是构建期静态的、四站共用，所以必须在这里于解析器插入 ze-snippet 之前摘掉——
  // 否则 www/za/sosovalue 的客服组件会永久失效，且是静默失效（useZenDesk 里 if(window.zE) 直接返回）。
  //
  // 时机：本插件在任何 app.head 脚本被解析之前同步执行（模块脚本仍是 deferred），
  // 因此这里的 DOM 查询是空集、MutationObserver 必定先于解析器看到 ze-snippet。
  const { isEu } = useSite()
  if (!unref(isEu)) {
    const restore = () => {
      const el = document.getElementById('ze-snippet') as HTMLScriptElement | null
      if (!el || !el.hasAttribute('type')) return
      // 仅摘掉属性不够：parser 已按 text/plain 处理过该元素，之后改属性不会让它重新加载
      // （实测：属性已摘掉，但 zdassets 请求数仍为 0）。
      // 必须换一个全新的 <script> 节点插入，浏览器才会重新走一遍「准备脚本」流程。
      const fresh = document.createElement('script')
      for (const a of Array.from(el.attributes)) {
        if (a.name === 'type' || a.name === 'data-cookieconsent') continue
        fresh.setAttribute(a.name, a.value)
      }
      el.replaceWith(fresh)
    }
    restore()
    new MutationObserver((_records, obs) => {
      restore()
      if (document.getElementById('ze-snippet')) obs.disconnect()
    }).observe(document.documentElement, { childList: true, subtree: true })
  }

  NuxtApp.vueApp.use({
    install(Vue: any) {
      Vue.component('VueCookieConsent', {
        render: () => null,
        methods: {
          loadCookiebotScript() {
            // 检查是否已加载
            if (document.getElementById('Cookiebot')) return;
            
            // 创建 script 元素
            const script = document.createElement('script');
            script.id = 'Cookiebot';
            script.src = 'https://consent.cookiebot.com/uc.js';
            script.setAttribute('data-cbid', '15743e75-b81f-460d-bafa-717e8a592ea2');
            // blockingmode 必须是 manual，不能用 auto。
            // auto 模式下 Cookiebot 会主动扫描并接管 DOM 中的 script/link（为 consent 前阻断做准备），
            // 这个接管动作会导致 Vite __vitePreload 新插入的 CSS link 的 load 事件不被派发。
            // 而 __vitePreload 用 Promise.all 等这个事件、且没有超时，Promise 永不 settle，
            // 结果是 Lazy* 异步组件的 loader 永不执行、组件永久停在 <!---->：
            // EU 站 KYC 表单整体空白（详见 WEB-3645）。
            //
            // ⚠️ 合规前提：auto 提供的「consent 前自动阻断非必要脚本」能力会因此失效。
            // 改用 manual 后，非必要脚本需自行标记阻断 —— Zendesk 的做法见 nuxt.config.ts：
            // type="text/plain" + data-cookieconsent。
            //
            // ⚠️ 不要用 data-cookieblock-src 标 <script>：那是 iframe/image 的机制
            // （WEB-3647 实测：拦得住但用户同意后不会释放，客服入口会永久失效）。
            script.setAttribute('data-blockingmode', 'manual');
            script.type = 'text/javascript';
            
            // 插入到 head 中（推荐）
            document.head.appendChild(script);
            
            // 或者插入到 body 开头
            // document.body.insertBefore(script, document.body.firstChild);
          }
        },
        mounted() {
          const { isEu } = useSite()
          if(unref(isEu)){
            // 动态加载 Cookiebot 脚本
            this.loadCookiebotScript();
            return;
          }
          const href = '/policy'
          const policy = `<a href="${href}" target="_blank" class="text-highlight">${this.$t('Privacy Policy and Cookie Policy')}</a>`

          import('cookieconsent').then(() => {
            window.cookieconsent.initialise({
              window: `<div role="dialog" aria-live="polite" aria-label="cookieconsent" aria-describedby="cookieconsent:desc" class="cc-window {{classes}}"><!--googleoff: all-->{{children}}<!--googleon: all--></div>`,
              palette: {
                popup: { background: 'rgba(30,32,63,.9)' },
                button: {
                  background: '#c5ab80',
                  border: 'transparent',
                  text: '#fff',
                },
                highlight: {
                  background: 'transparent',
                  border: 'rgba(255,255,255,.5)',
                  text: 'rgba(255,255,255,.5)',
                },
              },
              theme: 'classic',
              layouts: {
                legend: '{{header}}{{message}}{{compliance}}',
              },
              layout: 'legend' as any,
              position: 'bottom-right',
              compliance: {
                info: `<div class="cc-compliance cc-highlight tw-flex">{{deny}}{{allow}}</div>`,
                'opt-in': '<div class="cc-compliance cc-highlight">{{deny}}{{allow}}</div>',
                'opt-out': '<div class="cc-compliance cc-highlight">{{deny}}{{allow}}</div>',
              },
              type: 'info',
              content: {
                header: this.$t('WE_VALUE_YOUR_PRIVACY'),
                message: this.$t('privacy_content', { policy }),
                allow: this.$t('Accept'),
                deny: this.$t('Reject'),
                href,
              },
              elements: {
                header: `<span class="cc-header ">{{header}}</span>`,
                allow: `<a aria-label="allow cookies" role=button tabindex="0" class="cc-btn cc-allow ">{{allow}}</a>`,
                deny: `<a aria-label="deny cookies" role=button tabindex="0" class="cc-btn cc-deny">{{deny}}</a>`,
              },
              revokable: false,
              onStatusChange: function (status: Status) {
                // const popup = this as any;
                // console.log("status: %o", status);
                // console.log(
                //   popup.hasConsented() ? "enable cookies" : "disable cookies"
                // );
              },
            })
          })
        },
      })
    },
  })
})
